import React, { useMemo, useState } from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import {
  OpenInNew as DrillIcon,
  Lightbulb as TipIcon,
  CheckCircle as GoodIcon,
  WarningAmber as WarnIcon,
  ErrorOutline as BadIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import moment from 'moment-jalaali';
import JalaliDateField from '../components/common/JalaliDateField';
import type { PnlLine } from '../services/financeAccountingService';

// ---------------------------------------------------------------------------
// کیت مشترک صفحهٔ حسابداری: فرمت اعداد/تاریخ + بلوک‌های UI (کاشی، توضیح، کارت، ردیف حساب، بازه)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
export const fa = (s: string | number): string => String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
export const num = (n: number | null | undefined): string => (n == null || !isFinite(Number(n)) ? '—' : new Intl.NumberFormat('fa-IR').format(Math.round(Number(n))));
export const toman = (n: number | null | undefined): string => (n == null || !isFinite(Number(n)) ? '—' : `${num(n)} تومان`);
/** نمایش فشرده برای کاشی‌ها: ۱۲٫۳ میلیون تومان */
export const compact = (n: number | null | undefined): string => {
  if (n == null || !isFinite(Number(n))) return '—';
  const v = Number(n); const a = Math.abs(v);
  const f = (x: number, u: string) => `${new Intl.NumberFormat('fa-IR', { maximumFractionDigits: a >= 100 * 1e9 ? 0 : 1 }).format(x)} ${u} تومان`;
  if (a >= 1e9) return f(v / 1e9, 'میلیارد');
  if (a >= 1e6) return f(v / 1e6, 'میلیون');
  if (a >= 1e3) return `${num(v)} تومان`;
  return `${num(v)} تومان`;
};
export const pct = (part: number, whole: number): string => (whole > 0 ? `${new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 1 }).format((part / whole) * 100)}٪` : '—');
export const jd = (iso?: string | null, withTime = false): string => { if (!iso) return '—'; try { return fa(moment(iso).format(withTime ? 'jYYYY/jMM/jDD HH:mm' : 'jYYYY/jMM/jDD')); } catch { return '—'; } };
export const jToISO = (v: string, end = false): string | undefined => { const s = (v || '').trim(); if (!s) return undefined; const m = moment(s, 'jYYYY/jMM/jDD'); if (!m.isValid()) return undefined; return (end ? m.endOf('day') : m.startOf('day')).toISOString(); };
export const cls = (n: number) => (n > 0 ? 'fac-pos' : n < 0 ? 'fac-neg' : '');
export const LS_BEGINNER = 'fac-beginner-mode';

export const useBeginner = () => {
  const [on, setOn] = useState<boolean>(() => { try { return localStorage.getItem(LS_BEGINNER) !== '0'; } catch { return true; } });
  const toggle = () => { setOn((v) => { try { localStorage.setItem(LS_BEGINNER, v ? '0' : '1'); } catch { /* ignore */ } return !v; }); };
  return { on, toggle };
};

export const Explain: React.FC<{ show: boolean; title: string; text: React.ReactNode; how?: string[] }> = ({ show, title, text, how }) => {
  const [open, setOpen] = useState(true);
  if (!show) return null;
  return (
    <div className="fac-explain">
      <div className="fac-explain-title" onClick={() => setOpen(!open)}>
        <TipIcon /> {title}
        <span className="fac-explain-toggle">{open ? 'بستن' : 'نمایش توضیح'}</span>
      </div>
      {open && (
        <>
          <div>{text}</div>
          {how && how.length > 0 && <ul>{how.map((h, i) => <li key={i}>{h}</li>)}</ul>}
        </>
      )}
    </div>
  );
};

export interface Kpi { label: string; value: number | null | undefined; color?: 'green' | 'red' | 'orange' | 'blue' | 'purple' | 'gray'; help?: string; sub?: React.ReactNode; count?: boolean; raw?: React.ReactNode }
export const Kpis: React.FC<{ items: Kpi[] }> = ({ items }) => (
  <div className="fac-kpis">
    {items.map((k, i) => (
      <div key={i} className={`fac-kpi ${k.color || 'blue'}`}>
        <div className="fac-kpi-label">
          {k.label}
          {k.help && <Tooltip title={k.help} arrow placement="top"><span className="fac-kpi-help">؟</span></Tooltip>}
        </div>
        <div className={`fac-kpi-value ${k.raw ? 'small' : ''}`}>{k.raw ?? (k.count ? num(k.value) : compact(k.value))}</div>
        {!k.raw && !k.count && k.value != null && Math.abs(Number(k.value)) >= 1e6 && <div className="fac-kpi-full">{toman(k.value)}</div>}
        {k.sub && <div className="fac-kpi-sub">{k.sub}</div>}
      </div>
    ))}
  </div>
);

export type Tone = 'good' | 'warn' | 'bad' | 'info';
export const Insights: React.FC<{ items: Array<{ tone: Tone; text: React.ReactNode }> }> = ({ items }) => {
  if (!items.length) return null;
  const I = { good: GoodIcon, warn: WarnIcon, bad: BadIcon, info: InfoIcon };
  return (
    <div className="fac-insights">
      {items.map((it, i) => { const Ic = I[it.tone]; return <div key={i} className={`fac-insight ${it.tone}`}><Ic /><div>{it.text}</div></div>; })}
    </div>
  );
};

export const Card: React.FC<{ title: string; sub?: string; icon?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode }> = ({ title, sub, icon, actions, children }) => (
  <div className="fac-card">
    <div className="fac-card-head">
      {icon}<h3>{title}</h3>
      {actions && <div className="fac-actions">{actions}</div>}
      {sub && <p>{sub}</p>}
    </div>
    <div className="fac-card-body">{children}</div>
  </div>
);

export const Lines: React.FC<{ lines: PnlLine[]; total?: { title: string; amount: number }; onDrill?: (p: Record<string, string>) => void; tone?: 'pos' | 'neg' | 'none'; bars?: boolean }> = ({ lines, total, onDrill, tone = 'none', bars = true }) => {
  const max = Math.max(1, ...lines.map((l) => Math.abs(l.amount)));
  const barColor = tone === 'pos' ? 'var(--color-success)' : tone === 'neg' ? 'var(--color-error)' : 'var(--color-primary)';
  return (
    <div className="fac-lines">
      {lines.map((l) => (
        <div key={l.code} className={`fac-line ${l.drill && onDrill ? 'clickable' : ''}`} onClick={l.drill && onDrill ? () => onDrill(l.drill!) : undefined} title={l.drill && onDrill ? 'کلیک: اسناد این حساب در دفتر تراکنش‌ها' : undefined}>
          <span className="fac-line-code">{l.code}</span>
          <div>
            <div className="fac-line-title">{l.title}{l.drill && onDrill && <small><DrillIcon style={{ fontSize: 12, verticalAlign: 'middle' }} /> اسناد</small>}</div>
            <div className="fac-line-note">{l.note}</div>
            {bars && <div className="fac-bar"><i style={{ width: `${Math.max(1, (Math.abs(l.amount) / max) * 100)}%`, background: barColor }} /></div>}
          </div>
          <div className={`fac-line-amount ${tone === 'pos' ? 'fac-pos' : tone === 'neg' ? 'fac-neg' : ''}`}>
            {toman(l.amount)}
            {l.count != null && <span className="fac-line-count">{num(l.count)} مورد</span>}
          </div>
        </div>
      ))}
      {total && <div className="fac-line-total"><span>{total.title}</span><span className={tone === 'pos' ? 'fac-pos' : tone === 'neg' ? 'fac-neg' : ''}>{toman(total.amount)}</span></div>}
    </div>
  );
};

/** بازهٔ شمسی مشترک */
export const useRange = (defaultDays = 30) => {
  const [fromJ, setFromJ] = useState(moment().subtract(defaultDays, 'days').format('jYYYY/jMM/jDD'));
  const [toJ, setToJ] = useState(moment().format('jYYYY/jMM/jDD'));
  const [preset, setPreset] = useState<string>(defaultDays === 30 ? '30d' : defaultDays >= 365 ? 'year' : '');
  const iso = useMemo(() => ({ from: jToISO(fromJ), to: jToISO(toJ, true) }), [fromJ, toJ]);
  const quick = (kind: 'thisMonth' | 'lastMonth' | '30d' | '90d' | 'thisYear') => {
    const now = moment(); setPreset(kind);
    if (kind === 'thisMonth') { setFromJ(now.clone().startOf('jMonth' as any).format('jYYYY/jMM/jDD')); setToJ(now.format('jYYYY/jMM/jDD')); }
    else if (kind === 'lastMonth') { const m = now.clone().subtract(1, 'jMonth' as any); setFromJ(m.clone().startOf('jMonth' as any).format('jYYYY/jMM/jDD')); setToJ(m.clone().endOf('jMonth' as any).format('jYYYY/jMM/jDD')); }
    else if (kind === 'thisYear') { setFromJ(now.clone().startOf('jYear' as any).format('jYYYY/jMM/jDD')); setToJ(now.format('jYYYY/jMM/jDD')); }
    else { setFromJ(now.clone().subtract(kind === '90d' ? 90 : 30, 'days').format('jYYYY/jMM/jDD')); setToJ(now.format('jYYYY/jMM/jDD')); }
  };
  const label = `${fa(fromJ)} تا ${fa(toJ)}`;
  return { fromJ, toJ, setFromJ: (v: string) => { setPreset(''); setFromJ(v); }, setToJ: (v: string) => { setPreset(''); setToJ(v); }, iso, quick, preset, label };
};

export const RangeBar: React.FC<{ r: ReturnType<typeof useRange>; onApply: () => void; loading?: boolean; extra?: React.ReactNode }> = ({ r, onApply, loading, extra }) => (
  <div className="fac-card">
    <div className="fac-card-body">
      <div className="fac-range">
        <JalaliDateField label="از تاریخ (شمسی)" value={r.fromJ} onChange={r.setFromJ} />
        <JalaliDateField label="تا تاریخ (شمسی)" value={r.toJ} onChange={r.setToJ} />
        {extra}
        <Button variant="contained" size="small" onClick={onApply} disabled={loading} sx={{ height: 38 }}>{loading ? <CircularProgress size={16} color="inherit" /> : 'نمایش گزارش'}</Button>
        <div className="fac-quick">
          {[['thisMonth', 'این ماه'], ['lastMonth', 'ماه قبل'], ['30d', '۳۰ روز'], ['90d', '۹۰ روز'], ['thisYear', 'امسال']].map(([k, l]) => (
            <button key={k} className={r.preset === k ? 'on' : ''} onClick={() => r.quick(k as any)}>{l}</button>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const Tag: React.FC<{ color: 'green' | 'red' | 'orange' | 'blue' | 'gray'; children: React.ReactNode }> = ({ color, children }) => <span className={`fac-tag ${color}`}>{children}</span>;

